import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  VStack,
  HStack,
  Badge,
  Table,
  Checkbox,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@chakra-ui/react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import { useUnletteredLines, type UnletteredLine } from '../hooks/useOwnerAccounts'
import { useBankAccounts } from '@/features/banking/hooks/useBankAccounts'
import { toaster } from '@/shared/lib/toaster'
import type { OwnerBalance } from '../types'
import { Circle, CircleDot, CircleCheck } from 'lucide-react'

// Modes de règlement avec mapping compte PCG
// transitAccount: null = direct en 512 (banque sélectionnée)
// transitAccount: '5112' = chèques à encaisser
// transitAccount: '517' = Stripe (CB)
// transitAccount: '53' = Caisse (espèces)
// Tous les règlements copropriétaires vont dans le journal EC (Encaissements)
export const PAYMENT_MODES = [
  { value: 'bank_transfer', label: 'Virement bancaire', transitAccount: null, journal: 'EC' },
  { value: 'instant_transfer', label: 'Virement instantané', transitAccount: null, journal: 'EC' },
  { value: 'wero', label: 'Wero', transitAccount: null, journal: 'EC' },
  { value: 'sepa_debit', label: 'Prélèvement SEPA', transitAccount: null, journal: 'EC' },
  { value: 'card', label: 'Carte bancaire (Stripe)', transitAccount: '517', journal: 'EC' },
  { value: 'check', label: 'Chèque', transitAccount: '5112', journal: 'EC' },
  { value: 'cash', label: 'Espèces', transitAccount: '53', journal: 'EC' },
] as const

export type PaymentMode = (typeof PAYMENT_MODES)[number]['value']

// Helper pour obtenir le compte transitoire
export function getTransitAccount(mode: PaymentMode): string | null {
  const paymentMode = PAYMENT_MODES.find((m) => m.value === mode)
  return paymentMode?.transitAccount ?? null
}

// Helper pour obtenir le journal
export function getPaymentJournal(mode: PaymentMode): string {
  const paymentMode = PAYMENT_MODES.find((m) => m.value === mode)
  return paymentMode?.journal ?? 'EC'
}

interface PaymentModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
  unitGroup: {
    unitId: string
    unitNumber: string
    accountCode: string
    accountId: string | null
    owners: OwnerBalance[]
    totalBalance: number
    hasMultipleOwners: boolean
  } | null
  onSubmit: (data: PaymentFormData) => Promise<void>
}

export interface PaymentFormData {
  unitId: string
  payerId: string | null // ID du payeur (facultatif)
  amount: number
  paymentDate: string
  paymentMode: PaymentMode
  bankAccountId: string
  reference: string
  comment: string
  selectedLineIds: string[] // Lignes à lettrer (deprecated, remplacé par lettrageAmounts)
  lettrageAmounts: Record<string, number> // Nouveau: montants par ligne {lineId: amount}
  // Données enrichies pour la génération comptable
  ownerAccountId: string | null // ID du compte 450xxx
  payerName: string | null // Nom du payeur pour le libellé
}

export function PaymentModal({
  isOpen,
  onClose,
  residenceId,
  unitGroup,
  onSubmit,
}: PaymentModalProps) {
  // Form state
  const [formData, setFormData] = useState<PaymentFormData>({
    unitId: '',
    payerId: null,
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0] ?? '',
    paymentMode: 'bank_transfer',
    bankAccountId: '',
    reference: '',
    comment: '',
    selectedLineIds: [],
    lettrageAmounts: {},
    ownerAccountId: null,
    payerName: null,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Hooks
  const { data: unletteredLines, isLoading: isLoadingLines } = useUnletteredLines(
    unitGroup?.unitId,
    residenceId
  )

  const { data: bankAccounts } = useBankAccounts({
    residence_id: residenceId,
    is_active: true,
  })

  // Initialiser le formulaire quand le drawer s'ouvre
  useEffect(() => {
    if (isOpen && unitGroup) {
      const primaryBank = bankAccounts?.find((b) => b.is_primary) || bankAccounts?.[0]
      const firstOwner = unitGroup.owners[0]
      const payerName = unitGroup.owners.length === 1 && firstOwner
        ? `${firstOwner.first_name} ${firstOwner.last_name}`
        : null
      setFormData({
        unitId: unitGroup.unitId,
        payerId: unitGroup.owners.length === 1 && firstOwner ? firstOwner.owner_id : null,
        amount: Math.max(0, unitGroup.totalBalance), // Suggérer le solde débiteur
        paymentDate: new Date().toISOString().split('T')[0] ?? '',
        paymentMode: 'bank_transfer',
        bankAccountId: primaryBank?.id || '',
        reference: '',
        comment: '',
        selectedLineIds: [],
        lettrageAmounts: {},
        ownerAccountId: unitGroup.accountId,
        payerName,
      })
    }
  }, [isOpen, unitGroup, bankAccounts])

  // Auto-sélection des lignes à lettrer avec ventilation automatique
  useEffect(() => {
    if (unletteredLines && formData.amount > 0) {
      const selectedIds: string[] = []
      const amounts: Record<string, number> = {}
      let remainingPayment = formData.amount

      // Sélectionner et ventiler les lignes par ordre chronologique
      for (const line of unletteredLines) {
        if (remainingPayment <= 0.01) break

        // Montant à affecter = minimum entre restant du règlement et restant de la ligne
        const amountToAllocate = Math.min(remainingPayment, line.remaining_amount)

        if (amountToAllocate > 0.01) {
          selectedIds.push(line.id)
          amounts[line.id] = Math.round(amountToAllocate * 100) / 100
          remainingPayment -= amountToAllocate
        }
      }

      setFormData((prev) => ({
        ...prev,
        selectedLineIds: selectedIds,
        lettrageAmounts: amounts,
      }))
    }
  }, [unletteredLines, formData.amount])

  // Calculs
  const allocatedTotal = useMemo(() => {
    // Somme des montants alloués (ventilation)
    return Object.values(formData.lettrageAmounts).reduce((sum, amount) => sum + amount, 0)
  }, [formData.lettrageAmounts])

  const balanceStatus = useMemo(() => {
    const diff = formData.amount - allocatedTotal
    if (Math.abs(diff) < 0.01) return { status: 'balanced', diff: 0 }
    if (diff > 0) return { status: 'excess', diff } // Règlement > affectation = excédent
    return { status: 'over', diff: Math.abs(diff) } // Affectation > règlement = impossible
  }, [formData.amount, allocatedTotal])

  // Helpers
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR')
  }

  // Toggle une ligne dans la sélection
  const handleLineToggle = useCallback((line: UnletteredLine) => {
    setFormData((prev) => {
      const isSelected = prev.selectedLineIds.includes(line.id)

      if (isSelected) {
        // Désélectionner : retirer de la liste et supprimer le montant
        const newAmounts = { ...prev.lettrageAmounts }
        delete newAmounts[line.id]
        return {
          ...prev,
          selectedLineIds: prev.selectedLineIds.filter((id) => id !== line.id),
          lettrageAmounts: newAmounts,
        }
      } else {
        // Sélectionner : ajouter avec montant max par défaut
        const currentAllocated = Object.values(prev.lettrageAmounts).reduce((s, a) => s + a, 0)
        const remaining = prev.amount - currentAllocated
        const amountToAllocate = Math.min(remaining, line.remaining_amount)

        return {
          ...prev,
          selectedLineIds: [...prev.selectedLineIds, line.id],
          lettrageAmounts: {
            ...prev.lettrageAmounts,
            [line.id]: Math.max(0, Math.round(amountToAllocate * 100) / 100),
          },
        }
      }
    })
  }, [])

  // Modifier le montant alloué à une ligne
  const handleAmountChange = useCallback((lineId: string, amount: number, maxAmount: number) => {
    setFormData((prev) => {
      // Valider que le montant ne dépasse pas le restant à lettrer de la ligne
      const validAmount = Math.min(Math.max(0, amount), maxAmount)

      return {
        ...prev,
        lettrageAmounts: {
          ...prev.lettrageAmounts,
          [lineId]: Math.round(validAmount * 100) / 100,
        },
      }
    })
  }, [])

  const handleSubmit = async () => {
    // Validation
    if (!formData.amount || formData.amount <= 0) {
      toaster.create({
        title: 'Erreur',
        description: 'Le montant doit être supérieur à 0',
        type: 'error',
      })
      return
    }

    if (!formData.bankAccountId) {
      toaster.create({
        title: 'Erreur',
        description: 'Veuillez sélectionner un compte bancaire',
        type: 'error',
      })
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(formData)
      onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur lors de l'enregistrement"
      toaster.create({ title: 'Erreur', description: message, type: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!unitGroup) return null

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="xl">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="800px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Enregistrer un règlement</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={5} align="stretch">
              {/* Zone 1: Identification */}
              <Box p={4} bg="gray.50" borderRadius="md">
                <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
                  <Box>
                    <Text fontSize="sm" color="gray.600">
                      Lot
                    </Text>
                    <Text fontWeight="semibold">
                      {unitGroup.unitNumber} — {unitGroup.owners
                        .filter((o) => o.first_name)
                        .map((o) => `${o.first_name} ${o.last_name}`)
                        .join(' & ')}
                    </Text>
                    <Text fontSize="sm" fontFamily="mono" color="gray.500">
                      Compte {unitGroup.accountCode}
                    </Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="sm" color="gray.600">
                      Solde actuel
                    </Text>
                    <Text
                      fontSize="xl"
                      fontWeight="bold"
                      color={unitGroup.totalBalance > 0 ? 'red.600' : 'green.600'}
                    >
                      {unitGroup.totalBalance > 0 ? 'Débiteur' : 'Créditeur'} :{' '}
                      {formatCurrency(Math.abs(unitGroup.totalBalance))}
                    </Text>
                  </Box>
                </HStack>
              </Box>

              {/* Sélecteur du payeur (si plusieurs propriétaires) */}
              {unitGroup.hasMultipleOwners && (
                <Field label="Payeur">
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.payerId || ''}
                      onChange={(e) => {
                        const selectedOwner = unitGroup.owners.find(o => o.owner_id === e.target.value)
                        const payerName = selectedOwner
                          ? `${selectedOwner.first_name} ${selectedOwner.last_name}`
                          : null
                        setFormData({
                          ...formData,
                          payerId: e.target.value || null,
                          payerName,
                        })
                      }}
                    >
                      <option value="">Non précisé</option>
                      {unitGroup.owners
                        .filter((o) => o.owner_id && o.first_name)
                        .map((owner) => (
                          <option key={owner.owner_id} value={owner.owner_id!}>
                            {owner.first_name} {owner.last_name}
                            {owner.share_numerator && owner.share_denominator
                              ? ` (${owner.share_numerator}/${owner.share_denominator})`
                              : ''}
                          </option>
                        ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              )}

              {/* Zone 2: Informations du règlement */}
              <Flex gap={4} flexWrap="wrap">
                <Box flex={1} minW="150px">
                  <Field label="Montant" required>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })
                      }
                      placeholder="0,00"
                    />
                  </Field>
                </Box>

                <Box flex={1} minW="150px">
                  <Field label="Date de règlement" required>
                    <Input
                      type="date"
                      value={formData.paymentDate}
                      onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                    />
                  </Field>
                </Box>

                <Box flex={1} minW="150px">
                  <Field label="Mode de règlement" required>
                    <NativeSelectRoot>
                      <NativeSelectField
                        value={formData.paymentMode}
                        onChange={(e) =>
                          setFormData({ ...formData, paymentMode: e.target.value as PaymentMode })
                        }
                      >
                        {PAYMENT_MODES.map((mode) => (
                          <option key={mode.value} value={mode.value}>
                            {mode.label}
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                  </Field>
                </Box>
              </Flex>

              <Flex gap={4} flexWrap="wrap">
                <Box flex={2} minW="200px">
                  <Field label="Compte bancaire de destination" required>
                    <NativeSelectRoot>
                      <NativeSelectField
                        value={formData.bankAccountId}
                        onChange={(e) => setFormData({ ...formData, bankAccountId: e.target.value })}
                      >
                        <option value="">Sélectionner un compte</option>
                        {bankAccounts?.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.label}
                            {account.is_primary ? ' (Principal)' : ''}
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                  </Field>
                </Box>

                <Box flex={1} minW="150px">
                  <Field label="Référence">
                    <Input
                      value={formData.reference}
                      onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                      placeholder="N° chèque, réf. virement..."
                    />
                  </Field>
                </Box>
              </Flex>

              <Field label="Commentaire">
                <Input
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  placeholder="Notes internes (facultatif)"
                />
              </Field>

              {/* Zone 3: Affectation aux appels de fonds */}
              <Box>
                <Text fontWeight="semibold" mb={3}>
                  Affectation aux appels de fonds
                </Text>

                {isLoadingLines ? (
                  <Text color="gray.500">Chargement...</Text>
                ) : !unletteredLines?.length ? (
                  <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
                    <Text color="gray.500">Aucun appel de fonds en attente de règlement</Text>
                  </Box>
                ) : (
                  <>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                      <Table.Root size="sm">
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeader w="40px"></Table.ColumnHeader>
                            <Table.ColumnHeader w="90px">Date</Table.ColumnHeader>
                            <Table.ColumnHeader>Libellé</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" w="100px">
                              Dû
                            </Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" w="100px">
                              Reste
                            </Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" w="110px">
                              Affecter
                            </Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {unletteredLines.map((line) => {
                            const isSelected = formData.selectedLineIds.includes(line.id)
                            const allocatedAmount = formData.lettrageAmounts[line.id] || 0
                            const isPartiallyLettered = line.lettrage_status === 'partially_lettered'

                            return (
                              <Table.Row
                                key={line.id}
                                bg={isSelected ? 'blue.50' : undefined}
                                cursor="pointer"
                                onClick={() => handleLineToggle(line)}
                                _hover={{ bg: isSelected ? 'blue.100' : 'gray.50' }}
                              >
                                <Table.Cell onClick={(e) => e.stopPropagation()}>
                                  <Checkbox.Root
                                    checked={isSelected}
                                    onCheckedChange={() => handleLineToggle(line)}
                                    size="sm"
                                  >
                                    <Checkbox.HiddenInput />
                                    <Checkbox.Control />
                                  </Checkbox.Root>
                                </Table.Cell>
                                <Table.Cell fontSize="sm">
                                  {formatDate(line.transaction_date)}
                                </Table.Cell>
                                <Table.Cell>
                                  <HStack gap={1}>
                                    {/* Indicateur de statut de lettrage */}
                                    {isPartiallyLettered ? (
                                      <CircleDot size={14} color="orange" />
                                    ) : (
                                      <Circle size={14} color="gray" />
                                    )}
                                    <Text fontSize="sm" lineClamp={1}>
                                      {line.label || line.description}
                                    </Text>
                                  </HStack>
                                </Table.Cell>
                                <Table.Cell
                                  textAlign="right"
                                  fontFamily="mono"
                                  fontSize="sm"
                                  color="gray.500"
                                >
                                  {formatCurrency(line.debit)}
                                </Table.Cell>
                                <Table.Cell
                                  textAlign="right"
                                  fontFamily="mono"
                                  fontSize="sm"
                                  fontWeight="medium"
                                  color={isPartiallyLettered ? 'orange.600' : undefined}
                                >
                                  {formatCurrency(line.remaining_amount)}
                                </Table.Cell>
                                <Table.Cell onClick={(e) => e.stopPropagation()}>
                                  {isSelected ? (
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      max={line.remaining_amount}
                                      size="xs"
                                      w="90px"
                                      textAlign="right"
                                      fontFamily="mono"
                                      value={allocatedAmount || ''}
                                      onChange={(e) =>
                                        handleAmountChange(
                                          line.id,
                                          parseFloat(e.target.value) || 0,
                                          line.remaining_amount
                                        )
                                      }
                                      bg="white"
                                    />
                                  ) : (
                                    <Text
                                      fontSize="sm"
                                      color="gray.400"
                                      textAlign="right"
                                      fontFamily="mono"
                                    >
                                      —
                                    </Text>
                                  )}
                                </Table.Cell>
                              </Table.Row>
                            )
                          })}
                        </Table.Body>
                      </Table.Root>
                    </Box>

                    {/* Indicateur d'équilibre */}
                    <Box mt={3} p={3} borderRadius="md" bg={
                      balanceStatus.status === 'balanced' ? 'green.50' :
                      balanceStatus.status === 'excess' ? 'orange.50' : 'red.50'
                    }>
                      <HStack justify="space-between" flexWrap="wrap" gap={2}>
                        <HStack gap={4}>
                          <Text fontSize="sm">
                            Règlement : <strong>{formatCurrency(formData.amount)}</strong>
                          </Text>
                          <Text fontSize="sm">
                            Affecté : <strong>{formatCurrency(allocatedTotal)}</strong>
                          </Text>
                        </HStack>
                        <Badge
                          colorPalette={
                            balanceStatus.status === 'balanced' ? 'green' :
                            balanceStatus.status === 'excess' ? 'orange' : 'red'
                          }
                          size="lg"
                        >
                          {balanceStatus.status === 'balanced' && (
                            <HStack gap={1}>
                              <CircleCheck size={14} />
                              <Text>Équilibré</Text>
                            </HStack>
                          )}
                          {balanceStatus.status === 'excess' &&
                            `Excédent : ${formatCurrency(balanceStatus.diff)}`}
                          {balanceStatus.status === 'over' &&
                            `Dépassement : ${formatCurrency(balanceStatus.diff)}`}
                        </Badge>
                      </HStack>
                      {balanceStatus.status === 'excess' && (
                        <Text fontSize="xs" color="orange.700" mt={2}>
                          L'excédent restera en crédit sur le compte copropriétaire.
                        </Text>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!formData.amount || formData.amount <= 0}
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
